<template>
  <v-card-text>
    <v-radio-group @change="calculateValue()" v-model="dayType">
      <v-radio color="success" value="every">
        <template v-slot:label>
          <div>
            <div class="d-flex align-center">
              Every
              <input style="width: 20px;" :class="{ 'error--text': !validation.every.isValid, 'ml-2 py-1 pl-1 cron-input': true}" disabled value="*/" />
              <input @input="calculateValue()" @keydown.space.prevent style="width: 50px;" :class="{ 'error--text': !validation.every.isValid, 'mr-2 py-1 pr-1 cron-input': true }" type="number" v-model.number="val.every" hide-details />
              {{ val.every == 1 ? 'day' : 'days' }}
            </div>
            <div class="caption error--text" v-if="!validation.every.isValid">{{ validation.every.message}}</div>
          </div>
        </template>
      </v-radio>
      <v-radio color="success" value="atSpecific">
        <template v-slot:label>
          <div class="d-flex align-center">
            On day <input @input="calculateValue()" @keydown.space.prevent style="width: 50px;" class="mx-2 pa-1 cron-input" type="number" min="0" max="60" v-model.number="val.atSpecific" hide-details /> 
            of the month
          </div>
        </template>
      </v-radio>
      <v-radio color="success" value="betweenDays">
        <template v-slot:label>
          <div class="d-flex align-center">
            Between day
            <input @input="calculateValue()" @keydown.space.prevent style="width: 50px;" class="mx-2 pa-1 cron-input" type="number" v-model.number="val.betweenDaysStart" />
            and
            <input @input="calculateValue()" @keydown.space.prevent style="width: 50px;" class="mx-2 pa-1 cron-input" type="number" v-model.number="val.betweenDaysEnd" hide-details />
            of the month
          </div>
        </template>
      </v-radio>
      <v-radio color="success" value="custom">
        <template v-slot:label>
          <div class="d-flex align-center">
            A custom, comma-delimited combination of the above:
            <input @input="calculateValue()" @keydown.space.prevent style="width: 200px; text-align: left;" class="mx-2 pa-1 cron-input" v-model="val.custom" hide-details />
          </div>
        </template>
      </v-radio>
    </v-radio-group>
  </v-card-text>
</template>

<script>
  const everyRegexLax = /^(\*)([^\s^\,]*)$/g
  const everyRegexCorrect = /^(\*)(\/([1-9]|1[0-9]|2[0-9]|3[0-1]))?$/g

  const atSpecificLax = /^(\d)*$/g
  const atSpecificCorrect = /^([1-9]|1[0-9]|2[0-9]|3[0-1])$/g

  const betweenDaysLax = /^([\d]*)-([\d]*)$/g
  const betweenDaysCorrect = /^([1-9]|1[0-9]|2[0-9]|3[0-1])-([1-9]|1[0-9]|2[0-9]|3[0-1])$/g

  const customLax = /^((\d+,)+\d+|(\d+-\d+,?)|\d+|(\*(\/\d+)?),?)+$/g
  const customCorrect = /^(([1-9]|1[0-9]|2[0-9]|3[0-1]+,)+[1-9]|1[0-9]|2[0-9]|3[0-1]+|([1-9]|1[0-9]|2[0-9]|3[0-1]+-[1-9]|1[0-9]|2[0-9]|3[0-1]+,?)|[1-9]|1[0-9]|2[0-9]|3[0-1]+|(\*(\/[1-9]|1[0-9]|2[0-9]|3[0-1]+)?),?)+$/g

  const normalizeOutput = (val) => val.toString().replace(' ', '')

  export default {
    props: {
      value: [String],
    },
    data: () => ({
      dayType: 'every',
      validation: {
        every: {
          isValid: true,
          message: 'Invalid syntax',
        },
        atSpecific: {
          isValid: true,
        },
        betweenDays: {
          isValid: true,
        },
        custom: {
          isValid: true,
        },
      },
      val: {
        every: 5,
        atSpecific: 10,
        betweenDaysStart: 10,
        betweenDaysEnd: 12,
        custom: '*/10,10-12',
      },
      output: null,
    }),
    watch: {
      value: {
        immediate: true,
        handler(val) {
          Object.values(this.validation).forEach(item => item.isValid = true)
          let match

          if (this.dayType != 'custom') {
            match = val.match(everyRegexLax)
            if (match) {
              this.dayType = 'every'
              match = val.match(everyRegexCorrect)
              if (!match) this.validation.every.isValid = false

              let normalized = val.replace('*', '')
              if (normalized.trim().length == 0) normalized = '1'
              else normalized = normalized.replace('/', '')

              this.val.every = normalized
              return
            }

            match = val.match(atSpecificLax)
            if (match) {
              this.dayType = 'atSpecific'
              this.val.atSpecific = val
              match = val.match(atSpecificCorrect)
              if (!match) this.validation.atSpecific.isValid = false
              return
            }

            match = val.match(betweenDaysLax)
            if (match) {
              this.dayType = 'betweenDays'
              let split = val.split('-')
              this.val.betweenDaysStart = split[0]
              this.val.betweenDaysEnd = split[1]
              match = val.match(betweenDaysCorrect)
              if (!match) this.validation.betweenDays.isValid = false
              return
            }
          }

          match = val.match(customLax)
          if (match) {
            this.dayType = 'custom'
            this.val.custom = val

            match = val.match(customCorrect)
            if (!match) this.validation.custom.isValid = false
            return
          } else {
            this.validation.custom.isValid = false
          }
        },
      },
      output(val) {
        this.$emit('input', val)
      },
      validation: {
        deep: true,
        handler(val) {
          let error = Object.values(val).some(item => !item.isValid)
          this.$emit('isValid', !error)
        },
      },
    },
    methods: {
      calculateValue() {
        switch (this.dayType) {
        case 'every':
          this.output = '*/' + normalizeOutput(this.val.every)
          break
        case 'atSpecific':
          this.output = normalizeOutput(this.val.atSpecific.toString())
          break
        case 'betweenDays':
          this.output = normalizeOutput(this.val.betweenDaysStart + '-' + this.val.betweenDaysEnd)
          break
        case 'custom':
          this.output = normalizeOutput(this.val.custom.toString())
          break
        default:
          this.output = '*'
          break
        }
      },
    },
  }
</script>

<style scoped>
  .cron-input {
    background-color: #f3f3f3;
    flex-grow: 0;
    text-align: center;
  }
</style>